import { Controller } from "../Controller.js";
import type { BoardDirective } from "./BoardDirective.js";
import type { Move } from "../../../../royalur/model/Move.js";
import type { Engine } from "../../../../royalur/engine/Engine.js";
import type { RoyalUrRoll } from "../../royalur/RoyalUrRoll.js";
import type { PlayerType } from "../../../../royalur/model/PlayerType.js";
import { MoveGameEvent } from "../../event/MoveGameEvent.js";
import { ListenerStore } from "../../../ListenerStore.js";
import type { NavigationGameEvent } from "../../event/NavigationGameEvent.js";
import { MoveBoardDirective } from "./MoveBoardDirective.js";
import { PlayBoardDirective } from "./PlayBoardDirective.js";
import type {
    ReplayMoveProperties } from "./ReplayMovesBoardDirective.js";
import {
    ReplayMovesBoardDirective,
} from "./ReplayMovesBoardDirective.js";
import type { MoveHighlight } from "./AnalysisBoardDirective.js";
import { AnalysisBoardDirective } from "./AnalysisBoardDirective.js";
import type { GameState } from "../../../../royalur/engine/state/GameState.js";


type MoveListener = (event: MoveGameEvent) => void;
type NavigationListener = (event: NavigationGameEvent) => boolean;


export interface ReplayMove {
    moveIndex: number;
    state: GameState;
    move: Move;
}


/**
 * Controls the interactions with the dice user interface.
 */
export class BoardController extends Controller<BoardDirective> {

    private musicEnabled: boolean = false;

    private readonly moveListeners: ListenerStore<MoveListener>;
    private readonly navigationListeners: ListenerStore<NavigationListener>;

    constructor() {
        super();
        this.moveListeners = new ListenerStore<MoveListener>();
        this.navigationListeners = new ListenerStore<NavigationListener>();
    }

    isMusicEnabled(): boolean {
        return this.musicEnabled;
    }

    setMusicEnabled(musicEnabled: boolean) {
        this.musicEnabled = musicEnabled;
    }

    override setup(): () => void {
        return () => {};
    }

    override onDiscardedDirectives(_directives: BoardDirective[]): void {
        // Nothing to do.
    }

    addMoveListener(moveListener: MoveListener): () => void {
        return this.moveListeners.add(moveListener);
    }

    handleMove(move: Move, skipAnimation: boolean) {
        this.moveListeners.invoke(new MoveGameEvent(move, skipAnimation));
    }

    addNavigationListener(navigationListener: NavigationListener): () => void {
        return this.navigationListeners.add(navigationListener);
    }

    handleNavigation(event: NavigationGameEvent): boolean {
        const capture = this.navigationListeners.invokeAndCollect(event);
        for (const doCapture of capture) {
            if (doCapture)
                return true;
        }
        return false;
    }

    createMakeMove(
        rules: Engine,
        moveIndex: number,
        state: GameState,
        move: Move,
        moveWinsGame: boolean,
    ): BoardDirective[] {
        return [
            new MoveBoardDirective(
                this.assignNextDirectiveID(),
                rules, moveIndex, state, move, moveWinsGame, false,
            ),
            ...this.createWait(rules, moveIndex, state, move.getPlayer(), moveWinsGame),
        ];
    }

    createReplayMoves(
        rules: Engine,
        moves: ReplayMove[],
        moveProperties?: ReplayMoveProperties[],
    ): BoardDirective[] {
        if (moves.length <= 0)
            throw new Error("Requires at least one move");

        const moveDirectives: MoveBoardDirective[] = [];
        for (const { moveIndex, state, move } of moves) {
            moveDirectives.push(new MoveBoardDirective(
                this.assignNextDirectiveID(),
                rules, moveIndex, state, move, false, false,
            ));
        }

        const lastDir = moveDirectives[moveDirectives.length - 1];
        return [
            new ReplayMovesBoardDirective(
                this.assignNextDirectiveID(),
                rules, moveDirectives, moveProperties,
            ),
            ...this.createWait(
                rules, lastDir.moveIndex, lastDir.state, lastDir.getActivePlayer(), false,
            ),
        ];
    }

    createAnalysis(
        rules: Engine,
        moveIndex: number,
        state: GameState,
        previewMove: Move,
        moveHighlights: MoveHighlight[],
    ): BoardDirective[] {
        const player = previewMove.getPlayer();
        return [new AnalysisBoardDirective(
            this.assignNextDirectiveID(),
            rules, moveIndex, player, state, previewMove, moveHighlights,
        )];
    }

    createWait(
        rules: Engine,
        moveIndex: number,
        state: GameState,
        activePlayer: PlayerType,
        isGameWon: boolean,
    ): BoardDirective[] {
        return [new PlayBoardDirective(
            this.assignNextDirectiveID(),
            rules, moveIndex, activePlayer, state, null, null, isGameWon,
        )];
    }

    createPromptForMove(
        rules: Engine,
        moveIndex: number,
        state: GameState,
        roll: RoyalUrRoll,
        availableMoves: Move[],
    ): BoardDirective[] {
        if (availableMoves.length === 0)
            throw new Error("No available moves!");

        const player = availableMoves[0].getPlayer();
        return [new PlayBoardDirective(
            this.assignNextDirectiveID(),
            rules, moveIndex, player, state, roll, availableMoves, false,
        )];
    }
}
